import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Spinner } from 'reactstrap';
import { BiCheck, BiTime } from 'react-icons/bi';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { changeTier } from '../../api/billingApi';
import DialogBox from '../utility/dialogBox';

const PLANS = [
    {
        tier: 'solo',
        name: 'Solo',
        price: 12,
        color: '#10b981',
        features: [
            'Up to 3 members',
            'AI receipt scanning',
            'Quoted vs actual tracking',
            '75 receipt scans/mo',
        ],
    },
    {
        tier: 'pro',
        name: 'Pro',
        price: 29,
        color: '#26ada9',
        features: [
            'Up to 10 members',
            'Branded quote PDFs',
            'Send quotes by email',
            'Customer accept/decline',
            'Accounting integrations',
            '250 receipt scans/mo',
        ],
    },
    {
        tier: 'business',
        name: 'Business',
        price: 59,
        color: '#8b5cf6',
        features: [
            'Up to 20 members',
            'AI margin insights',
            'Branded quote PDFs',
            'Accounting integrations',
            '600 receipt scans/mo',
            'Priority support',
        ],
    },
];

const PRICE_FOR_TIER = { solo: 12, pro: 29, business: 59 };

export default function ChangePlanModal({ isOpen, toggle, currentTier, pendingTier }) {
    const queryClient = useQueryClient();
    const [submitting, setSubmitting] = useState(null);
    // pendingChange holds the tier the user is about to switch to once they
    // confirm the styled DialogBox; null when no confirmation is open.
    const [pendingChange, setPendingChange] = useState(null);

    const handlePick = (tier) => {
        if (tier === currentTier) return;
        if (pendingTier) return;
        const newPlanName = PLANS.find((p) => p.tier === tier)?.name ?? tier;
        const isUpgrade = PRICE_FOR_TIER[tier] > PRICE_FOR_TIER[currentTier];
        setPendingChange({ tier, isUpgrade, planName: newPlanName });
    };

    const closeConfirm = () => {
        if (submitting) return;
        setPendingChange(null);
    };

    const handleConfirm = async () => {
        if (!pendingChange) return;
        const { tier, planName } = pendingChange;
        setSubmitting(tier);
        try {
            const result = await changeTier(tier);
            // Refresh both billing queries so the settings UI reflects the
            // new state (immediate for upgrades, pending for downgrades).
            queryClient.invalidateQueries({ queryKey: ['billingUsage'] });
            queryClient.invalidateQueries({ queryKey: ['billingStatus'] });
            if (result.status === 'completed') {
                toast.success(`Plan changed to ${planName}.`);
            } else {
                const date = new Date(result.effectiveAt).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'long', year: 'numeric',
                });
                toast.success(`Switching to ${planName} on ${date}.`);
            }
            setPendingChange(null);
            toggle();
        } catch (err) {
            toast.error(err?.message ?? 'Couldn\'t change plan. Please try again.');
        } finally {
            setSubmitting(null);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>Change plan</ModalHeader>
            <ModalBody>
                {pendingTier && (
                    <div className="alert alert-info d-flex align-items-center gap-2">
                        <BiTime />
                        <span>
                            You already have a scheduled change to <strong>{pendingTier}</strong>.
                            Cancel that first if you want to switch to a different tier.
                        </span>
                    </div>
                )}

                <div className="row g-3">
                    {PLANS.map((plan) => {
                        const isCurrent = plan.tier === currentTier;
                        const isPending = plan.tier === pendingTier;
                        const isPlanLoading = submitting === plan.tier;
                        const disabled = isCurrent || isPending || submitting !== null || !!pendingTier;
                        return (
                            <div key={plan.tier} className="col-md-4">
                                <div
                                    className="border rounded p-3 h-100 d-flex flex-column"
                                    style={isCurrent ? { borderColor: plan.color, borderWidth: 2 } : undefined}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-semibold fs-5" style={{ color: plan.color }}>{plan.name}</span>
                                        {isCurrent && <span className="badge" style={{ background: plan.color }}>Current</span>}
                                        {isPending && <span className="badge bg-warning text-dark">Switching to</span>}
                                    </div>
                                    <div className="mb-2">
                                        <span className="fs-3 fw-bold">£{plan.price}</span>
                                        <span className="text-muted small">/month inc. VAT</span>
                                    </div>
                                    <ul className="list-unstyled small flex-grow-1 mb-3">
                                        {plan.features.map((f) => (
                                            <li key={f} className="d-flex align-items-start gap-1 mb-1">
                                                <BiCheck className="flex-shrink-0 mt-1" style={{ color: plan.color }} />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Button
                                        color={isCurrent ? 'secondary' : 'primary'}
                                        outline={isCurrent}
                                        block
                                        disabled={disabled}
                                        onClick={() => handlePick(plan.tier)}
                                        style={!isCurrent ? { backgroundColor: plan.color, borderColor: plan.color } : undefined}
                                    >
                                        {isPlanLoading
                                            ? <Spinner size="sm" />
                                            : isCurrent
                                                ? 'Your current plan'
                                                : isPending
                                                    ? 'Pending switch'
                                                    : `Switch to ${plan.name}`}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <p className="text-muted small mt-3 mb-0">
                    Upgrades take effect immediately and you're charged the prorated difference.
                    Downgrades take effect at the end of your current billing period —
                    you keep your current tier's benefits until then.
                </p>
            </ModalBody>

            <DialogBox
                isOpen={!!pendingChange}
                toggle={closeConfirm}
                title={pendingChange?.isUpgrade ? `Upgrade to ${pendingChange.planName}?` : `Schedule downgrade to ${pendingChange?.planName}?`}
                message={pendingChange?.isUpgrade
                    ? `You'll be charged the prorated difference today and your plan will switch to ${pendingChange.planName} immediately.`
                    : `You'll keep your current plan until your next renewal date, then switch to ${pendingChange?.planName}. You can cancel before then.`}
                buttons={[
                    {
                        label: 'Cancel',
                        color: 'secondary',
                        onClick: closeConfirm,
                        disabled: !!submitting,
                    },
                    {
                        label: 'Continue',
                        color: 'primary',
                        onClick: handleConfirm,
                        loading: !!submitting,
                    },
                ]}
            />
        </Modal>
    );
}
